import React, { useState, useEffect, useCallback } from 'react';
import Modal from 'react-modal';
import { Edit, Trash } from 'lucide-react';
import { fetchChildWeeklyExpenses, updateChildWeeklyExpenses } from '../../../api';
import MyButton from '../../basic/MyButton';
import './ManageWeeklyExpensesModal.css';

Modal.setAppElement('#root');

const ManageWeeklyExpensesModal = ({ isOpen, onClose, childId, onSaveSuccess }) => {
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isFormChanged, setIsFormChanged] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [newExpense, setNewExpense] = useState({ name: '', amount: '' });

  // Calculate total weekly expenses
  const totalExpenses = expenses.reduce((sum, exp) => sum + parseFloat(exp.amount || 0), 0);

  const fetchExpenses = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await fetchChildWeeklyExpenses(childId);
      setExpenses(data.map(exp => ({
        id: exp.expense_id,
        name: exp.expense_name,
        amount: exp.amount
      })));
    } catch (err) {
      console.error("Error fetching expenses:", err);
      setError('Failed to load weekly expenses. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [childId]);

  useEffect(() => {
    if (isOpen) {
      setIsFormChanged(false);
      setEditingId(null);
      setNewExpense({ name: '', amount: '' });
      fetchExpenses();
    }
  }, [isOpen, fetchExpenses]);

  const handleAddExpense = () => {
    if (!newExpense.name || !newExpense.amount) return;

    setExpenses(prev => [...prev, {
      id: `temp-${Date.now()}`,
      name: newExpense.name,
      amount: parseFloat(newExpense.amount)
    }]);
    setNewExpense({ name: '', amount: '' });
    setIsFormChanged(true);
  };

  const handleDeleteExpense = (id) => {
    setExpenses(prev => prev.filter(exp => exp.id !== id));
    setIsFormChanged(true);
  };

  const handleStartEdit = (id) => {
    setEditingId(id);
  };

  const handleSaveEdit = (id, updatedName, updatedAmount) => {
    setExpenses(prev => prev.map(exp => 
      exp.id === id 
        ? { ...exp, name: updatedName, amount: parseFloat(updatedAmount) }
        : exp
    ));
    setEditingId(null);
    setIsFormChanged(true);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');

    const formattedExpenses = expenses.map(exp => ({
      expense_id: exp.id?.toString().startsWith('temp-') ? undefined : exp.id,
      expense_name: exp.name,
      amount: exp.amount
    }));

    try {
      await updateChildWeeklyExpenses(childId, formattedExpenses);
      onSaveSuccess('Weekly expenses updated successfully.');
    } catch (err) {
      console.error("Error updating expenses:", err);
      setError('Failed to update expenses. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      contentLabel="Manage Weekly Expenses"
      className="wem-modal"
      overlayClassName="wem-overlay"
    >
      <div className="wem-content">
        <h2 className="wem-title">Manage Weekly Expenses</h2>
        
        {/* Info Banner */}
        <div className="wem-info-banner">
          <p>
            💡 Popular expense ideas: Streaming services (Netflix, Spotify),
            Pet care, Phone plan contribution, Gaming subscriptions
            or Personal care items.
          </p>
        </div>

        {loading ? (
          <div className="wem-loading-spinner" />
        ) : (
          <>
            {/* Add New Expense Form */}
            <div className="wem-add-form">
              <input
                type="text"
                placeholder="Expense name"
                value={newExpense.name}
                onChange={(e) => setNewExpense(prev => ({ ...prev, name: e.target.value }))}
                className="wem-input"
              />
              <div className="wem-amount-input">
                <span className="wem-currency">$</span>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={newExpense.amount}
                  onChange={(e) => setNewExpense(prev => ({ ...prev, amount: e.target.value }))}
                  className="wem-input"
                />
              </div>
              <MyButton
                text="Add"
                onClick={handleAddExpense}
                disabled={!newExpense.name || !newExpense.amount}
              />
            </div>

            {/* Expenses List */}
            <div className="wem-expenses-list">
              {expenses.length === 0 ? (
                <div className="wem-empty-state">
                  <svg className="wem-empty-icon" viewBox="0 0 24 24">
                    <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 012-2h2a2 2 0 012 2M9 5a2 2 0 002 2h2a2 2 0 002-2" />
                  </svg>
                  <p className="wem-empty-title">No weekly expenses yet</p>
                  <p className="wem-empty-description">Add your first weekly expense using the form above</p>
                </div>
              ) : (
                expenses.map(expense => (
                  <ExpenseItem
                    key={expense.id}
                    expense={expense}
                    isEditing={editingId === expense.id}
                    onEdit={handleStartEdit}
                    onDelete={handleDeleteExpense}
                    onSave={handleSaveEdit}
                    onCancel={handleCancelEdit}
                  />
                ))
              )}
            </div>

            {/* Total Section */}
            {expenses.length > 0 && (
              <div className="wem-total-section">
                <span>Total Weekly Expenses</span>
                <span className="wem-total-amount">${totalExpenses.toFixed(2)}</span>
              </div>
            )}

            {error && <div className="wem-error-message">{error}</div>}

            {/* Action Buttons */}
            <div className="wem-actions">
              <MyButton
                text="Cancel"
                onClick={onClose}
                backgroundColor="white"
                color="#dc3545"
                hoverBackgroundColor="#f8f8f8"
                hoverColor="#c82333"
              />
              <MyButton
                text="Save Changes"
                onClick={handleSubmit}
                disabled={loading || !isFormChanged}
                color="white"
                hoverColor="white"
              />
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

const ExpenseItem = ({ expense, isEditing, onEdit, onDelete, onSave, onCancel }) => {
  const [editName, setEditName] = useState(expense.name);
  const [editAmount, setEditAmount] = useState(expense.amount);

  if (isEditing) {
    return (
      <div className="wem-expense-item wem-expense-item--editing">
        <input
          type="text"
          value={editName}
          onChange={(e) => setEditName(e.target.value)}
          className="wem-input"
        />
        <div className="wem-amount-input">
          <span className="wem-currency">$</span>
          <input
            type="number"
            step="0.01"
            min="0"
            value={editAmount}
            onChange={(e) => setEditAmount(e.target.value)}
            className="wem-input"
          />
        </div>
        <div className="wem-expense-actions">
          <button
            onClick={() => onSave(expense.id, editName, editAmount)}
            className="wem-icon-button wem-icon-button--confirm"
          >
            ✓
          </button>
          <button
            onClick={onCancel}
            className="wem-icon-button wem-icon-button--cancel"
          >
            ✕
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="wem-expense-item">
      <div className="wem-expense-info">
        <h3 className="wem-expense-name">{expense.name}</h3>
        <p className="wem-expense-amount">${expense.amount.toFixed(2)} weekly</p>
      </div>
      <div className="wem-expense-actions">
        <button
          onClick={() => onEdit(expense.id)}
          className="wem-icon-button"
        >
          <Edit size={18} />
        </button>
        <button
          onClick={() => onDelete(expense.id)}
          className="wem-icon-button"
        >
          <Trash size={18} />
        </button>
      </div>
    </div>
  );
};

export default ManageWeeklyExpensesModal;